import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Link from '@mui/material/Link';
import Zoom from '@mui/material/Zoom';
import { toast } from 'react-toastify';
import { httpGet, httpPost, httpDelete } from '../../HttpRequestHandling/httpRequestHandler';
import PaymentTermsMaps from './PaymentTermsMaps';
import EnhancedDataGrid from '../Common/EnhancedDataGrid';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddBoxIcon from '@mui/icons-material/AddBox';
import Tooltip from '@mui/material/Tooltip';
import { GridRowModes, useGridApiRef, GridActionsCellItem, } from '@mui/x-data-grid';
import DoneIcon from '@mui/icons-material/Done';
import CancelIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { getFilter, setFilter } from '../Common/Utilities';


const Transition = React.forwardRef(function Transition(props, ref) {
    return <Zoom in={true} ref={ref} {...props} />;
});

export default function PaymentTerms(props) {

    const [filterText, setFilterText] = useState(getFilter(['SystemSetup', 'PaymentTerms', 'searchText']) || '');
    const apiGridRef = useGridApiRef();
    const [resdata, setData] = React.useState([]);
    const [lastUpdated, setLastUpdated] = useState("");
    const [openPaymentTermsMapsDialog, setOpenPaymentTermsMapsDialog] = useState(false);
    const [selectedTermsCode, setSelectedTermsCode] = useState("");
    const [currentEditRowId, setCurrentEditRowId] = React.useState(null);
    const [isAdding, setIsAdding] = React.useState(null);
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = React.useState(false);
    const [deleteRowData, setDeleteRowData] = React.useState(null);
    const [isButtonDisabled, setIsButtonDisabled] = React.useState(false);
    const [rowModesModel, setRowModesModel] = React.useState({});
    const [sortModel, setSortModel] = React.useState(getFilter(['SystemSetup', 'PaymentTerms', 'sortModel']) || []);

    const refresh_clickHandler = async (event) => {
        setInProgress(true);
        try {
            let functionName = "getPaymentTerms";
            let messageName = "Payment Terms";
            const response = await httpGet("/api/Adapter/getSyncData/" + functionName);
            const data = response.data;
            setInProgress(false);
            if (data) {
                toast.success(messageName + 'Fetched Successfully');
                setData(data.records.map((e, i) => ({ ...e, rowId: i }))); // Update data state with fetched data
            } else {
                toast.error('Error fetching ', messageName);
            }
        } catch (error) {
            setInProgress(false);
            toast.error('Error fetching ', messageName);
        }
    };

    const editPaymentTermsMaps_clickHandler = (event) => {
        event.preventDefault();
        setOpenPaymentTermsMapsDialog(true);
        setSelectedTermsCode(event.target.dataset.termscode);
    };

    const paymentTermsMapsDialog_closeHandler = (event) => {
        setOpenPaymentTermsMapsDialog(false);
    };

    const fetchPaymentTerms = () => {
        props.inProgress(true);
        httpGet('/api/setup/paymentterms')
            .then((response) => {
                var data = response.data;
                var localDateString = data.lastUpdated == null ? "" : new Date(data.lastUpdated).toLocaleString();
                const records = (data.records || []).map((e, i) => ({ ...e, rowId: i }));
                setData(records);
                setLastUpdated(localDateString);
                props.inProgress(false);
            })
            .catch((error) => {
                props.inProgress(false);
                toast.error('An error occurred while fetching the records');
                console.error('There was an error!', error);
            });
    };

    useEffect(() => {

        const gridRef = apiGridRef?.current;
        if (gridRef && typeof gridRef.setQuickFilterValues === 'function') {
            gridRef.setQuickFilterValues([filterText]);
        }
        fetchPaymentTerms();
    }, []);

    const getRefreshButton = () => {
        return (
            <IconButton
                variant="contained"
                onClick={refresh_clickHandler}
                size="small"
                style={{ float: "right", zIndex: 1, marginTop: 10, display: 'none' }}
            >
                <RefreshIcon />
            </IconButton>
        );
    };

    const getAddBoxButton = () => (
        <IconButton
            variant="contained"
            onClick={handleAddClick}
            size="small"
            style={{ float: "right", zIndex: 1, marginTop: 10, display: 'block' }}
            disabled={isButtonDisabled}
        >
            <Tooltip key={1} title={'add'}><AddBoxIcon key={2} /></Tooltip>
        </IconButton>
    );

    const handleAddClick = React.useCallback(() => {
        const id = ([...resdata]?.sort((a, b) => b.rowId - a.rowId)[0]?.rowId + 1) || 1;
        let model = {
            rowId: id,
            isNew: true
        };
        setData((prevData) => [model, ...prevData]);
        setCurrentEditRowId(id);
        setRowModesModel((prevModel) => ({ ...prevModel, [id]: { mode: GridRowModes.Edit, fieldToFocus: 'currencyCode' } }));
        setIsButtonDisabled(true);
        setIsAdding(true);
    }, [resdata, currentEditRowId]);

    const handleCancelClick = React.useCallback((id) => {
        setRowModesModel((prevModel) => ({
            ...prevModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        }));
        setCurrentEditRowId(null);
        setIsButtonDisabled(false);
        setIsAdding(false);
        setData((prevData) => prevData.filter((row) => !(row.rowId === id && row.isNew)));
    }, []);

    const handleEditClick = React.useCallback((id) => {
        setCurrentEditRowId(id);
        setIsButtonDisabled(true);
        setIsAdding(false);
        setRowModesModel((prevModel) => ({ ...prevModel, [id]: { mode: GridRowModes.Edit } }));
    }, []);

    const handleSaveClick = (newData) => {
        setRowModesModel({ ...rowModesModel, [newData.rowId]: { mode: GridRowModes.View } });
    };

    const handleDeleteClick = React.useCallback((rowData) => {
        setDeleteRowData(rowData);
        setOpenDeleteConfirmation(true);
    }, []);

    const deleteTermsCode = React.useCallback(() => {
        props.inProgress(true);
        const data = deleteRowData;
        const url = `/api/setup/deletepaymentterms?termsCode=${encodeURIComponent(data.termsCode)}`;

        setOpenDeleteConfirmation(false);
        setDeleteRowData(null);

        httpDelete(url)
            .then((response) => {
                props.inProgress(false);
                toast.success('Deleted Successfully');
                fetchPaymentTerms();
            })
            .catch((error) => {
                props.inProgress(false);
                if (error.response.data.includes("Cannot delete, record being referenced by other entities.")) {
                    toast.error("Cannot delete, record being referenced by other entities.");
                } else {
                    toast.error(<div>Delete Failed<br />{error.response.data}</div>);
                }
            });
    }, [deleteRowData, fetchPaymentTerms]);

    const closeDeleteDialog = React.useCallback(() => {
        setOpenDeleteConfirmation(false);
        setDeleteRowData(null);
    }, []);

    const isRecordValid = (newrow, existingPaymentTerms) => {
        let isValid = true;
        let message = "";
        const newTermsCode = newrow.termsCode?.trim().toLowerCase().replace(/\s/g, '');

        const termsCodeExists = existingPaymentTerms.some(row => {
            const existingTermsCode = row.termsCode?.trim().toLowerCase().replace(/\s/g, '');
            return existingTermsCode === newTermsCode && row.rowId !== newrow.rowId;
        });


        if (termsCodeExists) {
            toast.error('This terms code already exists');
            return false;
        }

        if (!newrow.termsCode || newrow.termsCode.trim().length === 0) {
            message += "Payment Terms Code, ";
        }

        if (!newrow.termsDesc || newrow.termsDesc.trim().length === 0) {
            message += "Payment Terms Desc, ";
        }
        if (
            newrow.daysDue !==0 &&
            (!Number.isInteger(Number(newrow.daysDue)) || newrow.daysDue === null ||
                newrow.daysDue === undefined ||
                newrow.daysDue === '' )
        ) {
            message += "valid Due Day(s), ";
        }
        if (newrow.discountPercent !== 0 && (newrow.discountPercent === null || newrow.discountPercent === undefined || newrow.discountPercent === '')){
            message += "valid Discount Percentage, ";
        }

        if (message.length > 0) {
            message = message.slice(0, -2);
            toast.error(`Please provide ${message}`);
            isValid = false;
        }

        return isValid;
    };

    const savePaymentTerms = React.useCallback((newdata, oldData) => {
        let rowdata = structuredClone(newdata);
        let endpoint = "/api/setup/savepaymentterms";

        props.inProgress(true);

        httpPost(endpoint, rowdata)
            .then((response) => {
                props.inProgress(false);
                newdata.isNew = toast.success('Added Successfully');
                fetchPaymentTerms();
                setIsButtonDisabled(false);
            })
            .catch((error) => {
                if (newdata?.isNew) {
                    setData((prev) => prev.filter(e => e.rowId !== newdata.rowId));
                } else {
                    setData((prev) => prev.map(e => e.rowId === newdata.rowId ? oldData : e));
                }
                props.inProgress(false);
                toast.error(<div>Payment Terms Update Failed<br />{error.response.data}</div>);
                setIsButtonDisabled(false);
            });
    }, [fetchPaymentTerms, props]);

    const processRowUpdate = React.useCallback((newrow, oldrow) => {
        if (!isRecordValid(newrow, resdata)) {
            setRowModesModel({ ...rowModesModel, [newrow.rowId]: { mode: GridRowModes.Edit } });
            throw new Error('');
        }
        setCurrentEditRowId(null);
        savePaymentTerms(newrow, oldrow);
        return newrow;
    }, [savePaymentTerms, resdata]);

    const info = {
        termsCode: selectedTermsCode,
        inProgress: props.inProgress
    };

    return (
        <div>
            <h1>Payment Terms</h1>
            <div className="form-group">
                <div style={{ display: 'block', overflow: 'auto', width: '100%' }}>
                    <EnhancedDataGrid
                        rows={resdata}
                        apiRef={apiGridRef}
                        disableColumnMenu
                        editMode="row"
                        pagination
                        pageSize={10}
                        disableSelectionOnClick
                        title={"Last Updated: " + lastUpdated}
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={(err) => { }}
                        rowModesModel={rowModesModel}
                        setFilterText={(text) => {
                            const trimmed = text?.trim() || '';
                            setFilterText(trimmed);
                            setFilter(['SystemSetup', 'PaymentTerms', 'searchText'], trimmed);
                        }}
                        filterText={filterText}
                        onSortModelChange={(model) => {
                            setSortModel(model);
                            setFilter(['SystemSetup', 'PaymentTerms', 'sortModel'], model);
                        }}
                        sortModel={sortModel}
                        customcomponent={
                            (<>
                                {getRefreshButton()}
                                {getAddBoxButton()}
                            </>)
                        }
                        columns={[
                            {
                                headerName: 'Code',
                                field: 'termsCode',
                                flex: 0.4,
                                cellClassName: "MuiInputBase-input MuiInput-input",
                                editable: isAdding,
                            },
                            {
                                headerName: 'Description',
                                field: 'termsDesc',
                                flex: 1,
                                cellClassName: "MuiInputBase-input MuiInput-input",
                                editable: true,
                            },
                            {
                                headerName: 'Days Due',
                                field: 'daysDue',
                                type: "number",
                                headerAlign: 'right',
                                align: 'right',
                                flex: 0.5,
                                width: 150,
                                valueGetter: ({ value }) => (value && Number(value)) || 0,
                                cellClassName: "MuiInputBase-input MuiInput-input",
                                editable: true,
                            },
                            {
                                headerName: 'Discount Percent',
                                field: 'discountPercent',
                                type: "number",
                                headerAlign: 'right',
                                align: 'right',
                                flex: 1,
                                width: 160,
                                valueGetter: ({ value }) => (value && Number(value)) || 0,
                                cellClassName: "MuiInputBase-input MuiInput-input",
                                editable: true,
                            },
                            {
                                headerName: 'Mappings',
                                headerAlign: 'center',
                                field: 'mappings',
                                resizable: false,
                                sortable: false,
                                align: 'center',
                                width: 150,
                                renderCell: rowData => {
                                    if (rowData.row.isNew) {
                                        return '';
                                    }
                                    return <Link data-termscode={rowData.row.termsCode} href="#" onClick={editPaymentTermsMaps_clickHandler}>Edit</Link>
                                }
                            },
                            {
                                field: 'actions',
                                type: 'actions',
                                headerName: 'Actions',
                                width: 120,
                                cellClassName: 'actions',
                                getActions: (rowData) => {
                                    const isInEditMode = rowModesModel[rowData.id]?.mode === GridRowModes.Edit;
                                    if (isInEditMode) {
                                        return [
                                            <GridActionsCellItem
                                                key={rowData.id + 1}
                                                icon={<Tooltip key={rowData.id + 2} title={'Save'}><DoneIcon key={rowData.id + 1} /></Tooltip>}
                                                label="Save"
                                                color="inherit"
                                                onClick={() => { handleSaveClick(rowData.row) }}
                                            />,
                                            <GridActionsCellItem
                                                key={rowData.id + 2}
                                                icon={<Tooltip key={rowData.id + 2} title={'Cancel'}><CancelIcon key={rowData.id + 1} /></Tooltip>}
                                                label="Cancel"
                                                className="textPrimary"
                                                onClick={() => { handleCancelClick(rowData.id) }}
                                                color="inherit"
                                            />,
                                        ];
                                    }
                                    return [
                                        <GridActionsCellItem
                                            key={rowData.id + 2}
                                            icon={<Tooltip key={rowData.id + 2} title={'Edit'}><EditIcon key={rowData.id + 1} /></Tooltip>}
                                            label="Edit"
                                            className="textPrimary"
                                            onClick={() => { handleEditClick(rowData.id) }}
                                            color="inherit"
                                            disabled={isButtonDisabled}
                                        />,
                                        <GridActionsCellItem
                                            key={rowData.id + 3}
                                            icon={<Tooltip key={rowData.id + 2} title={'Delete'}><DeleteIcon key={rowData.id + 1} /></Tooltip>}
                                            label="Delete"
                                            onClick={() => { handleDeleteClick(rowData.row) }}
                                            color="inherit"
                                            disabled={isButtonDisabled}
                                        />,
                                    ];
                                }
                            }
                        ]}
                        getRowId={(row) => row.rowId}
                        editable={
                            {
                                onRowAdd: newData =>
                                    new Promise((resolve, reject) => {
                                        newData.rowId = 0;
                                        newData.companyCode = parseInt(state.companyCode);

                                        if (isRecordValid(newData)) {
                                            savePaymentTerms(newData, resolve, reject);
                                        }
                                        else {
                                            reject();
                                        }
                                    }),
                                onRowUpdate: (newData, oldData) =>
                                    new Promise((resolve, reject) => {

                                        if (isRecordValid(newData)) {
                                            savePaymentTerms(newData, resolve, reject);
                                        }
                                        else {
                                            reject();
                                        }
                                    }),
                                onRowDelete: oldData =>
                                    new Promise((resolve, reject) => {
                                        deleteTermsCode(oldData, resolve, reject);
                                    }),
                                onRowAddCancelled: rowData =>
                                    new Promise((resolve, reject) => {
                                    }),
                                onRowUpdateCancelled: rowData =>
                                    new Promise((resolve, reject) => {
                                    })
                            }
                        }
                    />
                </div>
            </div>
            <Dialog open={openPaymentTermsMapsDialog} TransitionComponent={Transition} fullWidth maxWidth="lg">
                <DialogContent>
                    <PaymentTermsMaps info={info} onClose={paymentTermsMapsDialog_closeHandler} />
                </DialogContent>
            </Dialog>

            <Dialog
                open={openDeleteConfirmation}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                    Delete Record
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the code "{deleteRowData ? deleteRowData.termsCode : ""}" ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={deleteTermsCode} color="primary">
                        Yes
                    </Button>
                    <Button autoFocus onClick={closeDeleteDialog} color="primary">
                        No
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};